/**
 * lib/drawer — ОБЩИЙ механизм выезжающей панели (drawer) — ствол для всех drawer'ов
 * продукта (owner-directive 2026-05-30 «код через модули»; тикет start-page-redo-and-
 * help AC-13).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: глобальный nav-rail и второй рейл-оглавление «Помощи»
 * (lib/help-toc) обоим нужна ОДНА И ТА ЖЕ drawer-механика: открыть/закрыть классом
 * на shell-элементе, scrim-затемнитель (клик закрывает), Esc закрывает,
 * transform-slide + reduced-motion (отключение анимации) — в CSS. Чтобы НЕ городить
 * два расходящихся scrim+Escape-обработчика с нуля (REJECT-критерий §code-structure
 * §1), механика вынесена сюда ОДИН раз. Потребитель даёт shell (на который вешается
 * класс открытия), панель и scrim; модуль управляет состоянием open/closed и
 * закрытием по Esc/scrim. АНИМАЦИЯ и геометрия (transform/где панель) — в CSS
 * потребителя (этот модуль только переключает класс).
 *
 * SCOPE: состояние drawer (open/close), scrim-клик, Esc-закрытие, aria-expanded на
 *   кнопке-переключателе (опц.), focus-возврат (опц.). NOT IN SCOPE: вёрстка панели/
 *   scrim (CSS потребителя), список содержимого (потребитель), позиционирование
 *   (CSS), формула inline↔collapsed (это у потребителя — рейл/help-toc).
 *
 * Cross-references: тикет AC-13; lib/nav-rail (первый потребитель — глобальный рейл);
 *   lib/help-toc (второй уровень навигации «Помощи»); code-structure §1 (ствол→ветки).
 */

export interface DrawerOptions {
  /** Элемент, на который вешается класс открытия (shell/контейнер режима). */
  readonly shell: HTMLElement;
  /** CSS-класс на shell в открытом состоянии (потребитель стилизует анимацию по нему). */
  readonly openClass: string;
  /** Затемнитель: клик по нему закрывает drawer. */
  readonly scrim: HTMLElement;
  /**
   * Кнопка-переключатель (опц.): её `aria-expanded` синхронизируется с состоянием.
   * Если задана — модуль обновляет aria-expanded на open/close.
   */
  readonly toggle?: HTMLElement;
  /** Вызывается при каждой смене состояния (для доп-логики потребителя). */
  readonly onChange?: (open: boolean) => void;
}

export interface DrawerHandle {
  open(): void;
  close(): void;
  toggle(): void;
  readonly isOpen: boolean;
  /** Снять слушатели + класс открытия (при размонтировании потребителя). */
  detach(): void;
}

/**
 * Смонтировать drawer-механику на готовые элементы потребителя. Возвращает handle
 * (open/close/toggle/detach). Esc закрывает (когда открыт); клик по scrim закрывает.
 * Анимация/transform — в CSS потребителя по `openClass` (+ `prefers-reduced-motion`
 * там же отключает её). Модуль НЕ создаёт DOM — он управляет уже существующим.
 */
export function attachDrawer(opts: DrawerOptions): DrawerHandle {
  let open = false;

  function setOpen(next: boolean): void {
    if (next === open) return;
    open = next;
    opts.shell.classList.toggle(opts.openClass, open);
    opts.toggle?.setAttribute('aria-expanded', open ? 'true' : 'false');
    opts.onChange?.(open);
  }

  function onScrimClick(): void {
    setOpen(false);
  }
  function onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      setOpen(false);
    }
  }

  opts.scrim.addEventListener('click', onScrimClick);
  document.addEventListener('keydown', onKeydown);
  // Стартовое состояние: закрыт (aria-expanded синхронизируем сразу).
  opts.toggle?.setAttribute('aria-expanded', 'false');

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!open),
    get isOpen(): boolean {
      return open;
    },
    detach(): void {
      opts.scrim.removeEventListener('click', onScrimClick);
      document.removeEventListener('keydown', onKeydown);
      opts.shell.classList.remove(opts.openClass);
    },
  };
}
